import { TFunction } from 'i18next';
import { TargetIcon } from '../SvgIcons/TargetIcon';
import { useUploadDroppedVideo } from '@/utils/upload-drop-video.utils';
import { useEffect } from 'react';
import { twMerge } from 'tailwind-merge';
import { Button } from '../Button';
import { VideoIconV2 } from '../SvgIcons/VideoIconV2';
import Modal from '../Modal';
import { showModal } from '@/utils/modal.utils';

interface VideoUploadProps {
  t: TFunction<undefined>;
  initialVideo?: string;
  allowedFormats: string[];
  maximumVideoSize?: number;
  disabledChooseAnotherVideoButton?: boolean;
  mutateUploadVideo?: (data: unknown) => void;
  onClick?: () => void;
}

export const VideoUpload = ({
  t,
  initialVideo,
  allowedFormats,
  maximumVideoSize,
  disabledChooseAnotherVideoButton,
  onClick,
  mutateUploadVideo,
}: VideoUploadProps) => {
  const {
    uploadedVideo: chosenVideo,
    errorMessage: chosenVideoErrorMessage,
    setUploadedVideo,
    chooseFileInputRef,
    handleFileDragOver,
    handleFileDrop,
    handleFileChange,
    handleClickChooseVideo,
  } = useUploadDroppedVideo({
    t,
    allowedFormats,
    maximumVideoSize,
  });

  useEffect(() => {
    if (initialVideo && !chosenVideo) {
      setUploadedVideo(initialVideo);
    }
  }, [initialVideo, chosenVideo, setUploadedVideo]);

  useEffect(() => {
    if (chosenVideoErrorMessage) {
      showModal('#video-upload-error-modal');
    }
  }, [chosenVideoErrorMessage]);

  useEffect(() => {
    if (chosenVideo && mutateUploadVideo && chosenVideo !== initialVideo) {
      const uploadFile = async (fileUrl: string) => {
        try {
          // Fetch the file from the URL
          const response = await fetch(fileUrl);
          const blob = await response.blob();

          // Create FormData and append the file
          const formData = new FormData();
          formData.append('file', blob, 'video.mp4');

          // Call mutateUploadVideo with the formData
          mutateUploadVideo(formData);
        } catch (error) {
          console.error('Error uploading video:', error);
        }
      };

      uploadFile(chosenVideo);
    }
  }, [chosenVideo, mutateUploadVideo, initialVideo]);

  return (
    <>
      {chosenVideo && (
        <div className="flex flex-1 justify-center gap-3">
          <div>
            <Button
              buttonClassName="px-5 py-4.5 flex-1 border-2 border-purple-5433FF rounded-full bg-white"
              text={t('common:chooseAnotherVideo')}
              textClassName="text-purple-5433FF"
              leftIcon={
                <VideoIconV2 className="-mt-[0.5px] w-6 h-6.5 [&>rect]:fill-purple-5433FF [&>path]:fill-white" />
              }
              disabled={disabledChooseAnotherVideoButton}
              onClick={(e) => {
                e.preventDefault();
                handleClickChooseVideo();
              }}
            />
          </div>
        </div>
      )}
      <div
        onDragOver={handleFileDragOver}
        onDrop={handleFileDrop}
        className={twMerge(
          'w-full flex flex-col justify-center items-center border-2 border-gray-300 border-dashed rounded-lg',
          chosenVideo && 'border-0 bg-black',
        )}
      >
        {chosenVideo ? (
          <video
            src={chosenVideo}
            controls
            className="w-full h-[350px] object-contain rounded-lg"
          />
        ) : (
          <div className="p-8">
            <div className="flex justify-center items-center">
              <TargetIcon className="w-10 h-10 [&>path]:fill-gray-1E1E1E" />
            </div>
            <div className="mt-3 flex justify-center text-gray-1E1E1E text-xl font-medium text-center">
              {t('common:dragAndDropVideo')}
            </div>
            <div className="mt-3 flex justify-center text-gray-1E1E1E text-sm uppercase">
              {t('common:or')}
            </div>
            <div
              className="mt-3 px-3.5 py-1.5 border border-black/20 rounded-md text-gray-1E1E1E text-sm text-center hover:cursor-pointer max-sm:leading-tight"
              onClick={onClick || handleClickChooseVideo}
            >
              {t('common:searchFolderAndUploadVideo')}
            </div>
            <div className="mt-3 text-gray-600 text-2sm text-center">
              {/* {t('common:maxAllowedWeight')} {maximumVideoSize} {t('common:mb')}
              <br /> */}
              {`${t('common:format')} ${allowedFormats.join(
                `, ${t('common:or').toLowerCase()} `,
              )}`}
            </div>
          </div>
        )}
        <input
          type="file"
          ref={chooseFileInputRef}
          className="hidden"
          accept="video/*"
          onChange={handleFileChange}
        />
      </div>
      <Modal
        id="video-upload-error-modal"
        title={t('common:videoIsIncorrect')}
        centerY
        size="small"
        showCancelBtn={false}
        showSecondBtn
        secondBtnLabel={t('common:ok')}
      >
        <p>{chosenVideoErrorMessage}</p>
      </Modal>
    </>
  );
};
